import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scale, ShieldCheck, FileLock2, Save, RotateCcw, X, Plus, Eye, History, User, Calendar, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { legalAPI } from '@/services/api';
import { toast } from 'sonner';

const LANG_LABELS = { en: 'English', pt: 'Portugues', es: 'Espanol', fr: 'Francais', de: 'Deutsch', pl: 'Polski' };
const ALL_LANGS = ['en', 'pt', 'es', 'fr', 'de', 'pl'];

const SLUG_META = {
	terms: { icon: Scale, key: 'legal.terms_title' },
	privacy: { icon: ShieldCheck, key: 'legal.privacy_title' },
	gdpr: { icon: FileLock2, key: 'legal.gdpr_title' },
};

const emptyTrans = () => ({ title: '', body: '' });

export default function LegalAdminManager() {
	const { t } = useTranslation();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activeSlug, setActiveSlug] = useState('terms');
	const [activeLang, setActiveLang] = useState('en');
	const [drafts, setDrafts] = useState({}); // { slug: { lang: { title, body } } }
	const [saving, setSaving] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [history, setHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);

	const reload = async () => {
		setLoading(true);
		try {
			const r = await legalAPI.listAdmin();
			const arr = r.data?.items || [];
			setItems(arr);
			// Hydrate drafts from server
			const next = {};
			for (const d of arr) {
				next[d.slug] = {};
				for (const [lang, v] of Object.entries(d.translations || {})) {
					next[d.slug][lang] = { title: v.title || '', body: v.body || '' };
				}
			}
			setDrafts(next);
		} catch {
			toast.error(t('common.error'));
		}
		setLoading(false);
	};

	useEffect(() => { reload(); }, []);

	const currentDraft = drafts[activeSlug] || {};
	const presentLangs = Object.keys(currentDraft);
	const missingLangs = ALL_LANGS.filter((l) => !presentLangs.includes(l));
	const tr = currentDraft[activeLang] || emptyTrans();

	const setField = (field, value) => {
		setDrafts((prev) => ({
			...prev,
			[activeSlug]: {
				...(prev[activeSlug] || {}),
				[activeLang]: { ...(prev[activeSlug]?.[activeLang] || emptyTrans()), [field]: value },
			},
		}));
	};

	const addTranslation = (lang) => {
		setDrafts((prev) => ({
			...prev,
			[activeSlug]: { ...(prev[activeSlug] || {}), [lang]: emptyTrans() },
		}));
		setActiveLang(lang);
	};

	const removeTranslation = (lang) => {
		if (lang === 'en') return;
		setDrafts((prev) => {
			const slugDrafts = { ...(prev[activeSlug] || {}) };
			delete slugDrafts[lang];
			return { ...prev, [activeSlug]: slugDrafts };
		});
		if (activeLang === lang) setActiveLang('en');
	};

	const save = async () => {
		const slugDrafts = drafts[activeSlug] || {};
		if (!slugDrafts.en?.title?.trim() || !slugDrafts.en?.body?.trim()) {
			toast.error(t('admin.legal_must_have_en'));
			return;
		}
		// strip empty non-en
		const cleaned = {};
		for (const [lang, v] of Object.entries(slugDrafts)) {
			if (lang === 'en' || (v.title?.trim() && v.body?.trim())) {
				cleaned[lang] = { title: v.title.trim(), body: v.body.trim() };
			}
		}
		setSaving(true);
		try {
			await legalAPI.update(activeSlug, { translations: cleaned });
			toast.success(t('common.success'));
			reload();
		} catch (e) {
			toast.error(e?.response?.data?.detail || t('common.error'));
		} finally {
			setSaving(false);
		}
	};

	const resetCurrent = () => {
		const original = items.find((d) => d.slug === activeSlug);
		if (!original) return;
		setDrafts((prev) => {
			const next = { ...prev, [activeSlug]: {} };
			for (const [lang, v] of Object.entries(original.translations || {})) {
				next[activeSlug][lang] = { title: v.title || '', body: v.body || '' };
			}
			return next;
		});
		toast.success(t('admin.legal_reset_done'));
	};

	const reseed = async () => {
		if (!window.confirm(t('admin.legal_reseed_confirm'))) return;
		try {
			await legalAPI.seed(true);
			toast.success(t('common.success'));
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const openHistory = async () => {
		setHistoryOpen(true);
		setHistoryLoading(true);
		try {
			const r = await legalAPI.audit({ slug: activeSlug, limit: 50 });
			setHistory(r.data?.items || []);
		} catch {
			setHistory([]);
		}
		setHistoryLoading(false);
	};

	if (loading) return <div className="text-sm text-[#666055]">Loading...</div>;

	return (
		<div className="space-y-4" data-testid="admin-legal-manager">
			<div className="flex items-center justify-between gap-3 flex-wrap">
				<div className="text-sm text-[#666055]">{t('admin.legal_subtitle')}</div>
				<Button
					size="sm"
					variant="outline"
					onClick={reseed}
					className="border-[#E6E2D6] text-[#9B2C2C] rounded-sm h-9 text-xs"
					data-testid="admin-legal-reseed-btn"
				>
					<RotateCcw className="w-3 h-3 mr-1" /> {t('admin.legal_reseed')}
				</Button>
			</div>

			{/* Document selector */}
			<Tabs value={activeSlug} onValueChange={(v) => { setActiveSlug(v); setActiveLang('en'); }}>
				<TabsList className="bg-[#F2F0E9] p-1 flex flex-wrap h-auto">
					{Object.entries(SLUG_META).map(([slug, meta]) => {
						const Icon = meta.icon;
						return (
							<TabsTrigger
								key={slug}
								value={slug}
								className="data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white text-xs px-4 py-2 rounded-sm gap-2"
								data-testid={`admin-legal-doc-${slug}`}
							>
								<Icon className="w-3.5 h-3.5" /> {t(meta.key)}
							</TabsTrigger>
						);
					})}
				</TabsList>

				{Object.keys(SLUG_META).map((slug) => (
					<TabsContent key={slug} value={slug} className="mt-4">
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-5 space-y-4">
							{/* Language tabs */}
							<Tabs value={activeLang} onValueChange={setActiveLang}>
								<div className="flex items-center gap-3 flex-wrap">
									<TabsList className="bg-[#F2F0E9] p-1 flex-wrap h-auto">
										{presentLangs.map((l) => (
											<TabsTrigger
												key={l}
												value={l}
												className="data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-sm"
												data-testid={`admin-legal-lang-${l}`}
											>
												{LANG_LABELS[l] || l}
												{l !== 'en' && (
													<span
														className="ml-2 text-[#9B2C2C] hover:text-[#7B1F1F]"
														onClick={(e) => { e.stopPropagation(); removeTranslation(l); }}
														data-testid={`admin-legal-lang-remove-${l}`}
													>
                            <X className="w-3 h-3 inline" />
                          </span>
												)}
											</TabsTrigger>
										))}
									</TabsList>

									{missingLangs.length > 0 && (
										<div className="flex items-center gap-1 flex-wrap">
											{missingLangs.map((l) => (
												<button
													key={l}
													type="button"
													onClick={() => addTranslation(l)}
													className="text-[11px] px-2 py-1 border border-[#E6E2D6] rounded-sm text-[#1A3C34]
															hover:bg-[#F2F0E9] inline-flex items-center gap-1"
													data-testid={`admin-legal-lang-add-${l}`}
												>
													<Plus className="w-3 h-3" /> {LANG_LABELS[l] || l}
												</button>
											))}
										</div>
									)}

									<Button
										size="sm"
										variant="outline"
										onClick={openHistory}
										className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 text-xs ml-auto"
										data-testid="admin-legal-history-btn"
									>
										<History className="w-3 h-3 mr-1" /> {t('admin.legal_history')}
									</Button>
									<Button
										size="sm"
										variant="outline"
										onClick={() => setPreviewOpen(true)}
										className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 text-xs"
										data-testid="admin-legal-preview-btn"
									>
										<Eye className="w-3 h-3 mr-1" /> {t('admin.legal_preview')}
									</Button>
								</div>

								{/* Editor */}
								<div className="mt-4 space-y-3">
									<div>
										<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
											{t('admin.legal_title')} ({LANG_LABELS[activeLang] || activeLang})
										</label>
										<Input
											value={tr.title}
											onChange={(e) => setField('title', e.target.value)}
											maxLength={200}
											className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
											data-testid={`admin-legal-title-${activeLang}`}
										/>
									</div>
									<div>
										<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
											{t('admin.legal_body')} ({LANG_LABELS[activeLang] || activeLang})
										</label>
										<Textarea
											value={tr.body}
											onChange={(e) => setField('body', e.target.value)}
											maxLength={60000}
											rows={22}
											placeholder={t('admin.legal_body_placeholder')}
											className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm font-mono text-xs leading-relaxed"
											data-testid={`admin-legal-body-${activeLang}`}
										/>
										<p className="text-[11px] text-[#666055]/70 mt-1">
											{t('admin.legal_markdown_hint')}
										</p>
									</div>
								</div>
							</Tabs>

							{/* Footer actions */}
							<div className="flex justify-end gap-2 pt-3 border-t border-[#E6E2D6]">
								<Button
									variant="ghost"
									onClick={resetCurrent}
									className="text-[#666055]"
									data-testid="admin-legal-reset-btn"
								>
									<RotateCcw className="w-4 h-4 mr-1" /> {t('admin.legal_reset')}
								</Button>
								<Button
									onClick={save}
									disabled={saving}
									className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm"
									data-testid="admin-legal-save-btn"
								>
									<Save className="w-4 h-4 mr-1" /> {saving ? t('common.loading') : t('admin.legal_save')}
								</Button>
							</div>
						</div>
					</TabsContent>
				))}
			</Tabs>

			{/* Preview Dialog */}
			<Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
				<DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-legal-preview-dialog">
					<DialogHeader>
						<DialogTitle className="text-[#1A3C34] font-['Playfair_Display']">
							{tr.title || '(no title)'} — {LANG_LABELS[activeLang] || activeLang}
						</DialogTitle>
					</DialogHeader>
					<article className="legal-prose pt-2">
						<ReactMarkdown>{tr.body || ''}</ReactMarkdown>
					</article>
				</DialogContent>
			</Dialog>

			{/* History Dialog */}
			<Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
				<DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-legal-history-dialog">
					<DialogHeader>
						<DialogTitle className="text-[#1A3C34] font-['Playfair_Display'] flex items-center gap-2">
							<History className="w-5 h-5 text-[#8B4513]" /> {t('admin.legal_history')} — {t(SLUG_META[activeSlug]?.key)}
						</DialogTitle>
					</DialogHeader>
					{historyLoading ? (
						<div className="text-sm text-[#666055] py-6 text-center">Loading...</div>
					) : history.length === 0 ? (
						<div className="text-sm text-[#666055] py-6 text-center" data-testid="admin-legal-history-empty">
							{t('admin.legal_history_empty')}
						</div>
					) : (
						<ul className="divide-y divide-[#E6E2D6] -mx-2">
							{history.map((h, i) => {
								const diff = h.diff_summary || {};
								return (
									<li key={i} className="py-3 px-2" data-testid={`admin-legal-history-row-${i}`}>
										<div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded-sm ${
												h.action === 'reseed' ? 'bg-[#9B2C2C] text-white' : 'bg-[#1A3C34] text-white'
											}`}>
                        {t(`admin.legal_audit_${h.action}`)}
                      </span>
											<span className="text-[11px] text-[#666055] inline-flex items-center gap-1">
                        <Tag className="w-3 h-3" /> {h.version_before || '—'} → <strong className="text-[#1A3C34]">{h.version_after}</strong>
                      </span>
											<span className="text-[11px] text-[#666055]/70 inline-flex items-center gap-1 ml-auto">
                        <Calendar className="w-3 h-3" /> {(h.created_at || '').slice(0, 19).replace('T', ' ')}
                      </span>
										</div>
										<div className="text-[11px] text-[#666055] inline-flex items-center gap-1 mb-1">
											<User className="w-3 h-3" /> {h.admin_name || h.admin_email || '—'}
										</div>
										<div className="text-[11px] text-[#3a3a3a] flex flex-wrap gap-x-3 gap-y-1">
											{diff.modified_langs?.length > 0 && (
												<span>
                          <span className="text-[#666055]">{t('admin.legal_audit_modified')}:</span>{' '}
													<strong className="text-[#1A3C34]">{diff.modified_langs.join(', ')}</strong>
                        </span>
											)}
											{diff.added_langs?.length > 0 && (
												<span>
                          <span className="text-[#666055]">{t('admin.legal_audit_added')}:</span>{' '}
													<strong className="text-[#2F855A]">{diff.added_langs.join(', ')}</strong>
                        </span>
											)}
											{diff.removed_langs?.length > 0 && (
												<span>
                          <span className="text-[#666055]">{t('admin.legal_audit_removed')}:</span>{' '}
													<strong className="text-[#9B2C2C]">{diff.removed_langs.join(', ')}</strong>
                        </span>
											)}
											{typeof diff.total_chars === 'number' && (
												<span className="text-[#666055]">
                          {t('admin.legal_audit_total_chars', { n: diff.total_chars })}
                        </span>
											)}
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}